import React from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import ScheduleIcon from '@material-ui/icons/Schedule';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import PlaceIcon from '@material-ui/icons/Place';
import LocalCafeIcon from '@material-ui/icons/LocalCafe';

const TNO_URL = 'https://www.tnonetwork.com';

const CALENDAR_DATES = [9, 16, 23, 30];
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function getMondayBasedIndex(date) {
  // JS: 0=Dom ... 6=Sáb  -> queremos 0=Lun ... 6=Dom
  const js = date.getDay();
  return (js + 6) % 7;
}

function CalendarMay2026() {
  const year = 2026;
  const monthIndex = 4; // Mayo
  const daysInMonth = 31;
  const firstDayIndex = getMondayBasedIndex(new Date(year, monthIndex, 1));
  const slots = [];
  for (let i = 0; i < firstDayIndex; i += 1) slots.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) slots.push(d);
  while (slots.length % 7 !== 0) slots.push(null);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        p: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.06,
          background:
            'radial-gradient(circle at 20% 20%, rgba(0,0,0,0.35), transparent 45%), radial-gradient(circle at 80% 30%, rgba(0,0,0,0.18), transparent 45%)',
          pointerEvents: 'none',
        }}
      />
      <Box position="relative">
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1.5}>
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                flex: '0 0 auto',
              }}
            >
              <ScheduleIcon />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.7 }}>
                Cronograma
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                Mayo 2026 — 4 sábados
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
            {CALENDAR_DATES.map((d) => (
              <Box
                key={d}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontWeight: 900,
                  fontSize: 12,
                  lineHeight: 1,
                }}
              >
                {String(d).padStart(2, '0')}
              </Box>
            ))}
          </Box>
        </Box>

        <Grid container spacing={0.75} sx={{ mb: 1 }}>
          {WEEKDAYS.map((w) => (
            <Grid key={w} item xs={12 / 7}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  fontWeight: 800,
                  opacity: 0.75,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                {w}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={0.75}>
          {slots.map((d, idx) => {
            const isSelected = typeof d === 'number' && CALENDAR_DATES.includes(d);
            const isEmpty = d == null;
            return (
              <Grid key={`${d ?? 'x'}-${idx}`} item xs={12 / 7}>
                <Box
                  sx={{
                    height: 38,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: isEmpty ? '1px solid transparent' : '1px solid',
                    borderColor: isEmpty ? 'transparent' : 'divider',
                    bgcolor: isSelected ? 'primary.main' : isEmpty ? 'transparent' : 'background.paper',
                    color: isSelected ? 'primary.contrastText' : 'text.primary',
                    fontWeight: isSelected ? 900 : 700,
                    opacity: isEmpty ? 0 : 1,
                  }}
                >
                  {!isEmpty ? d : '0'}
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1.5 }}>
          Los días resaltados corresponden a las sesiones presenciales.
        </Typography>
      </Box>
    </Paper>
  );
}

const INFO_CARDS = [
  {
    title: 'Horario',
    value: '2:00 pm – 5:00 pm',
    detail: '4 sesiones presenciales (3 horas c/u).',
    Icon: AccessTimeIcon,
  },
  {
    title: 'Sede',
    value: 'Las Mercedes, Caracas',
    detail: (
      <span>
        En{' '}
        <a href={TNO_URL} target="_blank" rel="noopener noreferrer">
          TNO Network
        </a>{' '}
        (el primer ecosistema de comunicaciones digitales de Venezuela).
      </span>
    ),
    Icon: PlaceIcon,
  },
  {
    title: 'Networking',
    value: 'Coffee Break incluido',
    detail:
      'Entorno seguro con otros profesionales para fortalecer alianzas y compartir un rato agradable.',
    Icon: LocalCafeIcon,
  },
];

const DetallesExperiencia = () => {
  return (
    <Box id="detalles" py={{ xs: 2, md: 3 }}>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            variant="overline"
            color="primary"
            display="block"
            gutterBottom
          >
            Detalles de la experiencia
          </Typography>
          <Box component={Typography} fontWeight={900} variant="h4" gutterBottom sx={{ lineHeight: 1.15 }}>
            4 sábados para potenciar tu presencia y tu mensaje
          </Box>
          <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 560 }}>
            Diseñado para atención personalizada y práctica real. Cupos limitados.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography
              sx={{ textTransform: 'uppercase', fontWeight: 800, letterSpacing: 0.8 }}
              variant="overline"
              color="primary"
              align="center"
              display="block"
              gutterBottom
            >
              Cronograma
            </Typography>
            <CalendarMay2026 />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {INFO_CARDS.map(({ title, value, detail, Icon }) => (
                <Grid key={title} item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box display="flex" gap={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          flex: '0 0 auto',
                        }}
                      >
                        <Icon />
                      </Box>
                      <Box>
                        <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.6 }}>
                          {title}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                          {value}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {detail}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DetallesExperiencia;

